import { useEffect, useMemo, useState } from 'react'

import type { ClientRole, UID } from 'agora-rtc-sdk-ng'

import type { AgoraRTCClientWrapper } from '@core/agoraRTC/libs/AgoraRTCClientWrapper'

export interface UseJoinChannelProps {
  agoraClient: AgoraRTCClientWrapper | undefined
  clientRole: ClientRole
  channelName: string
  token: string
  uid?: UID
}

interface UseJoinChannelReturn {
  leave: () => Promise<void> | undefined
}

/** Hook that joins the channel and manages token and role updates. */
export function useJoinChannel({
  agoraClient,
  channelName,
  token,
  clientRole,
  uid,
}: UseJoinChannelProps): UseJoinChannelReturn {
  const [joined, setJoined] = useState<boolean>(false)

  useEffect(() => {
    if (agoraClient && channelName && token && uid) {
      agoraClient.join(channelName, token, uid)
      setJoined(true)
    }
  }, [agoraClient, channelName, token, uid])

  useEffect(() => {
    if (agoraClient?.token && token && token !== agoraClient.token) {
      agoraClient.renewToken(token)
    }
  }, [agoraClient, token])

  useEffect(() => {
    agoraClient?.setClientRole(clientRole)
  }, [agoraClient, clientRole])

  return useMemo(
    () => ({
      leave: () => {
        if (joined) {
          return agoraClient?.leave()
        }
      },
    }),
    [agoraClient, joined],
  )
}
